import { z } from 'zod'

const nz = () => z.coerce.number()

/** Single line in StrategyFinancialBreakdown (matches backend CashflowLineItem). */
export const cashflowLineItemSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    amount_annual: nz(),
    amount_monthly: nz().nullable().optional(),
    notes: z.string().optional(),
  })
  .passthrough()

/** Backend StrategyFinancialBreakdown — strict core fields; extras allowed via passthrough. */
export const strategyFinancialBreakdownSchema = z
  .object({
    schema_version: z.string().optional(),
    strategy: z.string(),
    mortgage_interest_only: z.boolean().optional(),
    mortgage_rate_annual_nominal: nz(),
    ltv: nz(),
    line_items: z.array(cashflowLineItemSchema),
    deposit_cash: nz(),
    loan_amount: nz(),
    cash_to_complete: nz(),
    annual_gross_rent: nz().optional(),
    annual_no_i_pre_finance: nz().optional(),
    annual_pre_tax_cashflow: nz(),
    monthly_pre_tax_cashflow: nz(),
    gross_yield_pct: nz(),
    noi_yield_pct: nz(),
    net_yield_after_finance_pct: nz(),
    cash_on_cash_year1_pre_tax_pct: nz(),
  })
  .passthrough()

/** Monte Carlo percentile bundle (matches backend MonteCarloResult). */
export const monteCarloResultSchema = z
  .object({
    iterations: nz(),
    mean_return: nz(),
    std_deviation: nz(),
    percentile_5: nz(),
    percentile_25: nz(),
    percentile_50: nz(),
    percentile_75: nz(),
    percentile_95: nz(),
    probability_of_target_return: nz(),
    worst_case_scenario: nz(),
    best_case_scenario: nz(),
  })
  .passthrough()

export const monteCarloAssumptionsSchema = z
  .object({
    realism_level: nz().optional(),
    strategy: z.string().optional(),
    pre_tax: z.boolean().optional(),
    horizon_years: nz().optional(),
    target_return_threshold_pct: nz().optional(),
    notes: z.array(z.string()).optional(),
    detail_lines: z.array(z.string()).optional(),
  })
  .passthrough()

/** One strategy row from analyzed_strategies — validates nested breakdown when present. */
export const strategyAnalysisRowSchema = z
  .object({
    strategy: z.string(),
    strategy_name: z.string().optional(),
    financial_breakdown: strategyFinancialBreakdownSchema.nullable().optional(),
    estimated_roi_percent: nz().optional(),
    initial_capital_required: nz().optional(),
    recommended: z.boolean().optional(),
  })
  .passthrough()

export const monteCarloStrategyRowSchema = z
  .object({
    strategy: z.string(),
    realism_level: z.number().optional(),
    metric: z.string().optional(),
    metric_display_name: z.string().optional(),
    horizon_years: z.number().optional(),
    iterations: z.number().optional(),
    result: monteCarloResultSchema.optional(),
    assumptions: monteCarloAssumptionsSchema.optional(),
    year1_engine_cash_on_cash: monteCarloResultSchema.optional().nullable(),
  })
  .passthrough()

export const strategyRecommendationSchema = z
  .object({
    analyzed_strategies: z.array(strategyAnalysisRowSchema).optional(),
    recommended_strategy: z.string().optional(),
    recommended_strategy_name: z.string().optional(),
    recommendation_reasoning: z.string().optional(),
    monte_carlo: z
      .object({
        by_strategy: z.array(monteCarloStrategyRowSchema).optional(),
      })
      .passthrough()
      .optional()
      .nullable(),
  })
  .passthrough()

/** Core AnalysisResult fields for dashboard / landlord report (forward-compatible). */
export const analysisResultSchema = z
  .object({
    id: z.string(),
    trace_id: z.string().optional().nullable(),
    property_id: z.string().optional(),
    investment_score: z.number(),
    investment_signal: z.string(),
    score_confidence: z.number().min(0).max(1).optional().nullable(),
    confidence_level: z.number().optional().nullable(),
    validation_warnings: z.array(z.string()).optional(),
    outlier_flags: z.array(z.string()).optional(),
    data_quality: z
      .object({
        missing: z.array(z.string()).optional(),
        consistency_warnings: z.array(z.string()).optional(),
      })
      .optional()
      .nullable(),
    pdf_export_blocked: z.boolean().optional(),
    requires_review: z.boolean().optional(),
    assumption_version: z.string().optional(),
    metric_definitions_version: z.string().optional(),
    key_factors: z.array(z.string()).optional(),
    metrics: z
      .object({
        gross_yield: z.number(),
        net_yield: z.number(),
      })
      .passthrough(),
    property_snapshot: z.record(z.unknown()).optional().nullable(),
    strategy_analysis: strategyRecommendationSchema.nullable().optional(),
    audit: z.record(z.unknown()).optional().nullable(),
    research: z
      .object({
        agent_name: z.string().optional(),
        status: z.string().optional(),
        progress: z.number().optional(),
        crime_rate: z.string().optional(),
        research_data_quality: z.number().optional(),
      })
      .passthrough()
      .optional()
      .nullable(),
    valuation: z
      .object({
        estimated_value: z.number().optional(),
        value_range_low: z.number().optional(),
        value_range_high: z.number().optional(),
        confidence: z.number().optional(),
        comparables_used: z.number().optional(),
        comparables: z.array(z.record(z.unknown())).optional(),
        price_per_sqft_market_avg: z.number().optional(),
      })
      .passthrough()
      .optional()
      .nullable(),
    risk: z
      .object({
        overall_risk_score: z.number().optional(),
        risk_level: z.string().optional(),
        categories: z.array(z.record(z.unknown())).optional(),
        monte_carlo: monteCarloResultSchema.optional().nullable(),
      })
      .passthrough()
      .optional()
      .nullable(),
    compliance: z
      .object({
        aml_check_passed: z.boolean().optional(),
        epc_valid: z.boolean().optional(),
        planning_restrictions: z.array(z.string()).optional(),
      })
      .passthrough()
      .optional()
      .nullable(),
  })
  .passthrough()

export function safeParseAnalysisResult(data) {
  return analysisResultSchema.safeParse(data)
}

/** Validate breakdown alone (e.g. CashflowBreakdown guard). */
export function safeParseFinancialBreakdown(data) {
  return strategyFinancialBreakdownSchema.safeParse(data)
}
